import { Dropzone } from "dropzone";
import { hideElements, showElements } from "../utils/misc";
import { FILE_ACCEPTED_FORMATS } from "../utils/constants";
import fileThumbnail from "../../assets/images/icons/file.svg";
import { alertJsonResponseError, isTurboStreamResponse } from "../utils/http_utils";

const languageOptions = {
  dictCancelUpload: "Annuler",
  dictCancelUploadConfirmation: "Etes-vous sûr de vouloir annuler le chargement ?",
  dictDefaultMessage: "Déposez vos factures ou cliquez ici",
  dictFallbackMessage: "Votre navigateur ne supporte pas le drag & drop.",
  dictFileTooBig: "Le fichier est trop volumineux ({{filesize}}MB). Taille maximum: {{maxFilesize}}MB.",
  dictInvalidFileType: "Ce type de fichier n'est pas supporté.",
  dictMaxFilesExceeded: "Le nombre maximum de fichiers ({{maxFiles}}) a été dépassé.",
  dictRemoveFile: "Supprimer",
  dictResponseError: "Une erreur est survenue.",
  dictUploadCanceled: "Chargement annulé.",
  dictMissingFile: "Fichier manquant.",
};

export const DEFAULT_MAX_FILE_SIZE = 10;
const DEFAULT_MAX_FILES = 1;

const computedOptions = (uploadOptions) => {
  const maxFiles = uploadOptions.maxFiles || DEFAULT_MAX_FILES;
  const acceptedFiles = uploadOptions.acceptedFiles || FILE_ACCEPTED_FORMATS;
  const paramName = uploadOptions.paramName || "file";
  const clickable = uploadOptions.clickable || ".dropzone";
  const autoProcessQueue = uploadOptions.autoUpload || false;

  return {
    acceptedFiles: acceptedFiles,
    dictInvalidFileType: acceptedFormatsNotice(acceptedFiles.split(" ")),
    addRemoveLinks: true,
    autoProcessQueue: autoProcessQueue,
    maxFilesize: DEFAULT_MAX_FILE_SIZE,
    maxFiles: maxFiles,
    parallelUploads: maxFiles,
    uploadMultiple: maxFiles > DEFAULT_MAX_FILES,
    paramName: paramName,
    clickable: clickable,
    ...uploadOptions,
  };
};

const initDropzone = (dropzoneFormId, additionalLanguageOptions, uploadOptions) => {
  const options = computedOptions(uploadOptions);

  return new Dropzone(`#${dropzoneFormId}`, {
    ...languageOptions,
    ...options,
    ...additionalLanguageOptions,
  });
};

const initDropzoneBasicEvents = (dropzone, uploadImgLabel, dragAndDropContainer, removeFileOnError = true) => {
  dropzone.on("addedfile", (file) => {
    handleFilePreview(file);
    uploadImgLabel.classList.add("hidden");
    toggleDropzoneValidity(dropzone, true);
  });

  dropzone.on("addedfiles", () => {
    hideElements(progressBarElements(dropzone.element));
  });

  dropzone.on("removedfile", () => {
    if (dropzone.files.length === 0) {
      uploadImgLabel.classList.remove("hidden");
    }
    toggleDropzoneValidity(dropzone, true);
  });

  dropzone.on("processing", () => {
    showElements(progressBarElements(dropzone.element));
    hideElements(cancelBtnElements(dropzone.element));
  });

  dropzone.on("error", function (file, data, xhr) {
    if (removeFileOnError) {
      dropzone.removeFile(file);
    } else {
      resetDropzoneFiles(dropzone);
    }

    if (xhr?.response?.includes("turbo-stream")) {
      Turbo.renderStreamMessage(xhr.response);
      return;
    }

    if (xhr) {
      alertJsonResponseError(xhr.response, xhr.status);
      file.previewElement.querySelector(".dz-error-message").remove();
    } else {
      handleClientSideError(dropzone, data);
    }
  });

  dropzone.on("dragover", () => {
    if (dragAndDropContainer.dataset.hoverable) {
      activateDropzoneContainer(dragAndDropContainer);
    }
  });

  dropzone.on("dragleave", () => {
    deactivateDropzoneContainer(dragAndDropContainer);
  });

  dropzone.on("drop", () => {
    deactivateDropzoneContainer(dragAndDropContainer);
  });
};

const handleClientSideError = (dropzone, data) => {
  const errorContainer = dropzone.element.querySelector(".dropzone-errors");
  errorContainer.innerHTML = data;
};

const toggleDropzoneValidity = (dropzone, isValid) => {
  const errorContainer = dropzone.element.querySelector(".dropzone-errors");

  if (isValid) {
    errorContainer.innerHTML = "";
  } else {
    errorContainer.innerHTML = languageOptions.dictMissingFile;
  }
};

const handleFilePreview = (file) => {
  file.previewElement.querySelector(".dz-image img").setAttribute("src", fileThumbnail);
};

const progressBarElements = (dropzoneElement) => dropzoneElement.querySelectorAll(".dz-progress");

const cancelBtnElements = (dropzoneElement) => dropzoneElement.querySelectorAll(".dz-remove");

const dropzoneElement = (id) => Dropzone.forElement(`#${id}`);

const acceptedFormatsNotice = (array) => {
  const isSingleFormat = array.length === 1;

  return isSingleFormat
    ? `Seul le format ${array[0]} est accepté.`
    : `Seuls les formats ${array.slice(0, array.length - 1).join(" ")} et ${array.at(-1)} sont acceptés.`;
};

const activateDropzoneContainer = (container) => {
  container.classList.add("dragging");
};

const deactivateDropzoneContainer = (container) => {
  container.classList.remove("dragging");
};

const isEmpty = (dropzone) => {
  return dropzone.files.length === 0;
};

const hasDroppedFiles = (dropzone) => {
  return !isEmpty(dropzone);
};

const handleDropzoneUploadSuccess = (dropzone, form) => {
  if (dropzone.options.maxFiles > 1) {
    dropzone.on("successmultiple", (e, response) => {
      onDropzoneUploadSuccess(e[0], response, form);
    });
  } else {
    dropzone.on("success", (e, response) => {
      onDropzoneUploadSuccess(e, response, form);
    });
  }
};

const onDropzoneUploadSuccess = (e, response, form) => {
  if (isTurboStreamResponse(e.xhr)) {
    // If a file is uploaded via turbo-stream format, we need to call this function as the dropzone response type is JSON
    Turbo.renderStreamMessage(response);
    // We dispatch this custom event to hide the file selection form when uploading PDF sales invoices, by calling the #filesAdded action in the upload-modal controller
    form.dispatchEvent(new CustomEvent("turbo:submit-end", { detail: [e.xhr] }));
  } else {
    // A form submitted with dropzone does not listen to ajax events
    // So we are dispatching this event manually as the dropzone success event corresponds to an ajax:success if the form was submitted natively
    form.dispatchEvent(new CustomEvent("ajax:success", { detail: [e.xhr] }));
  }
};

const resetDropzoneFiles = (dropzone) => {
  dropzone.files.forEach((file) => (file.status = Dropzone.QUEUED));
};

export {
  acceptedFormatsNotice,
  initDropzone,
  dropzoneElement,
  progressBarElements,
  cancelBtnElements,
  initDropzoneBasicEvents,
  handleClientSideError,
  toggleDropzoneValidity,
  isEmpty,
  hasDroppedFiles,
  handleDropzoneUploadSuccess,
  resetDropzoneFiles,
};
